import { App, Form } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TagUserGroupMultiSelectForm } from '../../components/Forms/TagUserGroupMultiSelectForm';
import type Group from '../../interfaces/Group';
import type TagInterface from '../../interfaces/Tag';
import type User from '../../interfaces/User';

interface UseTagFormProps {
  tag: TagInterface | null;
  onSubmit: (newTag: TagInterface) => void;
}

/**
 * Custom hook to manage Tag form state and submission logic.
 *
 * - Handles form initialization and re-initialization when the tag changes.
 * - Provides color selection handling specific to the tagging feature.
 * - Applies interface limitations (e.g., DenyCompanyTag).
 * - Exposes Ant Design Form instance and containerRef for advanced UI usage.
 */
export const useTagForm = ({ tag, onSubmit }: UseTagFormProps) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [form] = Form.useForm();

  // Form.useWatch can be used for reactiveness, but not strictly necessary here,
  // so we include it if UI logic depends on it elsewhere.
  const userIds = Form.useWatch('userIds', form);
  const groupIds = Form.useWatch('groupIds', form);
  const publicTag = Form.useWatch('publicTag', form);

  // Checkboxes and user selection button are enabled only when publicTag is false (private tag)
  const isPrivateTag = publicTag === false;

  // State for unsaved users and groups
  const [unsavedUsers, setUnsavedUsers] = useState<User[]>([]);
  const [unsavedGroups, setUnsavedGroups] = useState<Group[]>([]);

  // Local state for userIds and groupIds to ensure they're available on submit
  // This is necessary because Ant Design Form doesn't include fields without Form.Item in onFinish values
  const [localUserIds, setLocalUserIds] = useState<number[]>([]);
  const [localGroupIds, setLocalGroupIds] = useState<number[]>([]);

  // Track if user has made changes to users/groups selection
  // This helps us decide whether to use usersAndGroupsCount as fallback
  const [hasModifiedUsersGroups, setHasModifiedUsersGroups] = useState(false);

  // Ref to the form container for potential UI focusing or scrolling.
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Handles form submission.
   * Maps form values to a TagInterface object and triggers onSubmit callback.
   * If editing, merges the updated values with the existing tag.
   *
   * @param values - Values submitted from the form.
   */
  const handleFinish = (values: any) => {
    // Use local state for userIds and groupIds as the source of truth
    // This ensures we have the values even if they're not in the form values
    const finalUserIds = localUserIds.length > 0 ? localUserIds : values.userIds || [];
    const finalGroupIds = localGroupIds.length > 0 ? localGroupIds : values.groupIds || [];

    // Calculate total count
    // Note: finalUserIds and finalGroupIds already include all selected items (including unsaved ones),
    // so we don't need to add unsavedUsers.length and unsavedGroups.length separately
    const totalCount = finalUserIds.length + finalGroupIds.length;

    // Construct a TagInterface, use existing tag id if editing, else default to 0.
    let newTag: TagInterface = {
      id: tag?.id ?? 0,
      publicTag: values.publicTag,
      fileVisible: values.fileVisible,
      title: values.title,
      color: values.color,
      contributor: values.contributor,
      reader: values.reader,
      external: values.external,
      userIds: finalUserIds,
      groupIds: finalGroupIds,
      usersAndGroupsCount: totalCount,
      isUsedForFileVisibility: values.isUsedForFileVisibility,
    };

    // If editing, merge with previous tag to preserve any other fields.
    if (tag) {
      newTag = { ...tag, ...newTag };
    }

    // Pass the processed TagInterface to onSubmit callback.
    onSubmit(newTag);
  };

  /**
   * Sets the color field of the form.
   * Used for manual color selection.
   * Triggers field validation and UI update.
   *
   * @param color - The new color string selected by the user.
   */
  const handleColorSelect = (color: string) => {
    form.setFieldValue('color', color);
  };

  /**
   * Opens the modal to select users and groups.
   */
  const handleOpenUserGroupSelection = () => {
    const modalInstance = modal.confirm({
      width: 600,
      footer: null,
      title: t('modal.tagUserGroupMultiSelectForm.title'),
      content: (
        <>
          <span>{t('modal.tagUserGroupMultiSelectForm.description')}</span>

          <TagUserGroupMultiSelectForm
            tagId={tag?.id}
            onSubmit={(
              users: number[],
              groups: number[],
              unsavedUsers?: User[],
              unsavedGroups?: Group[],
            ) => {
              modalInstance.destroy();

              // Update local state (source of truth)
              setLocalUserIds(users);
              setLocalGroupIds(groups);

              // Mark that user has made changes
              setHasModifiedUsersGroups(true);

              // Also update form values for Form.useWatch to work
              form.setFieldsValue({
                userIds: users,
                groupIds: groups,
              });

              // Update unsaved users and groups state
              setUnsavedUsers(unsavedUsers || []);
              setUnsavedGroups(unsavedGroups || []);
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
            unsavedUsers={unsavedUsers}
            unsavedGroups={unsavedGroups}
            // userMultiSelectProps={{
            //   selectedUserIds: form.getFieldValue('userIds') || [],
            //   selectedGroupIds: form.getFieldValue('groupIds') || [],
            // }}
          />
        </>
      ),
    });
  };

  // Calculate total count
  // Use local state as source of truth, fallback to form watch values
  // Note: userIds and groupIds already include all selected items (including unsaved ones),
  // so we don't need to add unsavedUsers.length and unsavedGroups.length separately
  const finalUserIdsForCount = localUserIds.length > 0 ? localUserIds : userIds || [];
  const finalGroupIdsForCount = localGroupIds.length > 0 ? localGroupIds : groupIds || [];
  const calculatedCount = finalUserIdsForCount.length + finalGroupIdsForCount.length;

  // If arrays are empty but tag has usersAndGroupsCount and user hasn't made changes yet,
  // use usersAndGroupsCount as fallback. This handles the case where the tag was loaded
  // with usersAndGroupsCount but userIds/groupIds arrays are empty (not yet loaded from API)
  const selectedCount =
    calculatedCount > 0 || hasModifiedUsersGroups || !tag
      ? calculatedCount
      : tag.usersAndGroupsCount || 0;

  /**
   * When the tag prop changes, update or reset the form fields.
   * If a tag is passed, initialize the form with its values.
   * Otherwise, reset the form to its default state.
   * publicTag defaults to false (private tag) if not provided.
   */
  useEffect(() => {
    if (tag) {
      const tagUserIds = tag.userIds || [];
      const tagGroupIds = tag.groupIds || [];

      // Update local state
      setLocalUserIds(tagUserIds);
      setLocalGroupIds(tagGroupIds);

      form.setFieldsValue({
        publicTag: tag.publicTag ?? false,
        title: tag.title,
        color: tag.color,
        contributor: tag.contributor,
        reader: tag.reader,
        external: tag.external,
        userIds: tagUserIds,
        groupIds: tagGroupIds,
        usersAndGroupsCount: tag.usersAndGroupsCount || 0,
        fileVisible: tag.fileVisible,
      });
      // Reset unsaved items when loading an existing tag
      setUnsavedUsers([]);
      setUnsavedGroups([]);
      // Reset modification flag when loading an existing tag
      setHasModifiedUsersGroups(false);
    } else {
      // Reset to default values for new tag
      setLocalUserIds([]);
      setLocalGroupIds([]);

      form.setFieldsValue({
        publicTag: true,
        contributor: true,
        reader: true,
        external: true,
        userIds: [],
        groupIds: [],
        usersAndGroupsCount: 0,
        fileVisible: true,
        color: '#5F6368',
      });
      // Reset unsaved items for new tag
      setUnsavedUsers([]);
      setUnsavedGroups([]);
      // Reset modification flag for new tag
      setHasModifiedUsersGroups(false);
    }
  }, [tag, form]);

  return {
    form, // Ant Design Form instance for controlling form actions.
    containerRef, // Ref for outside access to the form container DOM node.
    handleFinish, // Submit handler to transform and forward form data.
    handleColorSelect, // Manual color selection field setter.
    handleOpenUserGroupSelection, // Handler to open the user/group selection modal.
    selectedCount, // Count of selected users and groups.
    isPrivateTag, // Whether the tag is private (publicTag === false), used to enable/disable role checkboxes and user selection.
    t, // Translation function from react-i18next.
  };
};
